import { NgClass, isPlatformBrowser } from '@angular/common';
import { firstValueFrom } from 'rxjs';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
  ViewChild,
  afterNextRender,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import type { Lyric } from '@lyricstream/shared';
import {
  AppPageBase,
  BreadcrumbComponent,
  BreadcrumbService,
  ColorService,
  LANG_SIGNAL,
  LyricCardComponent,
  LyricService,
  SearchService,
  EnumRouteName
} from '@lyricstream/shared';
import { LyricRecueilListComponent } from '../../ui/lyric-recueil-list/lyric-recueil-list.component';
import { filterByQuery, sortByRelevance } from './recueil-search';

// ── Types ──────────────────────────────────────────────────────────────────────

type Lang = string;
type Sort = 'num' | 'alpha' | 'pop';
type View = 'grid' | 'list';

const BATCH = 24;
/** Pages de 100 au maximum côté serveur ; 6 pages couvrent largement le plus gros recueil. */
const SEARCH_PAGE_SIZE = 100;
const MAX_SEARCH_PAGES = 6;
const SEARCH_DEBOUNCE_MS = 250;

// ── Composant ─────────────────────────────────────────────────────────────────

@Component({
  selector: 'app-recueil',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass, BreadcrumbComponent, LyricCardComponent, LyricRecueilListComponent],
  templateUrl: './recueil.html',
  styleUrl: './recueil.scss',
  host: { ngSkipHydration: 'true' },
})
export class RecueilComponent extends AppPageBase implements AfterViewInit, OnDestroy {
  @ViewChild('searchInput') private searchInput!: ElementRef<HTMLInputElement>;

  protected readonly appLang = inject(LANG_SIGNAL);
  private readonly ngZone = inject(NgZone);
  private readonly host = inject(ElementRef);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly lyricService = inject(LyricService);
  private readonly searchService = inject(SearchService);
  private readonly colorService = inject(ColorService);
  private readonly breadcrumbService = inject(BreadcrumbService);
  private readonly route = inject(ActivatedRoute);

  // Préfixe d'URL actif (recueils ou groupe-ce) : fil d'Ariane sous la même URL
  private readonly basePath = this.route.snapshot.parent?.url[0]?.path ?? 'recueils';
  private readonly _recueilParam = signal(this.route.snapshot.paramMap.get('id') ?? '');

  // ── State signals ──────────────────────────────────────────────────────────
  // Langue de l'ONGLET Français/Kréyòl de cette page (filtre local des chants affichés) — distinct
  // de `lang` hérité de AppPageBase (langue de l'URL globale, lecture seule, utilisé pour
  // construire lyricHref). Même nom historiquement, renommé pour lever la collision : AppPageBase.
  // lang n'a pas de .set(), donc les deux ne peuvent pas être le même signal.
  readonly recueilLang = signal<Lang>('fr');
  readonly query = signal('');
  readonly sort = signal<Sort>('num');
  readonly filters = signal<{ chorale: 'all' | 'none' | 'with'; audio: boolean; sheet: boolean }>({
    chorale: 'none',
    audio: false,
    sheet: false,
  });
  readonly view = signal<View>('grid');
  /** Chants du recueil trouvés par Meilisearch pour `query` (null = pas de recherche serveur en cours ou échec). */
  readonly matchedIds = signal<ReadonlySet<string> | null>(null);
  readonly loaded = signal(BATCH);
  readonly loading = signal(false);

  // ── UI state ───────────────────────────────────────────────────────────────
  readonly filterMenuOpen = signal(false);
  readonly sortMenuOpen = signal(false);

  // ── Service data ───────────────────────────────────────────────────────────
  readonly lyricByRecueils = this.lyricService.lyricByRecueils;
  readonly recueilMeta = this.lyricService.recueilMeta;

  // ── Computed ───────────────────────────────────────────────────────────────
  readonly filteredSongs = computed(() => {
    const l = this.recueilLang();
    const q = this.query();
    const f = this.filters();
    let list = this.lyricByRecueils().filter((c) => c.language.toLowerCase() === l.toLowerCase());

    list = filterByQuery(list, q, this.matchedIds());
    if (f.chorale === 'none') list = list.filter((c) => !c.isChoir);
    if (f.chorale === 'with') list = list.filter((c) => c.isChoir);
    if (f.audio) list = list.filter((c) => c.hasAudioFiles);
    if (f.sheet) list = list.filter((c) => c.hasPartitionFile);

    const s = this.sort();
    // Pendant une recherche, le tri par défaut (numéro) cède la place à la pertinence : le chant qui correspond
    // le mieux au texte saisi passe en premier, le numéro ne départage que les ex æquo.
    const byRelevance = s === 'num' && q.trim() !== '' && this.matchedIds() !== null;
    if (byRelevance) list = sortByRelevance(list, this.matchedIds()!);
    else if (s === 'num')
      list = [...list].sort((a, b) => (a.position ?? a.idLegacy ?? 0) - (b.position ?? b.idLegacy ?? 0));
    if (s === 'alpha')
      list = [...list].sort((a, b) =>
        a.title.localeCompare(b.title, 'fr', { sensitivity: 'base' }),
      );
    if (s === 'pop') list = [...list].sort((a, b) => (b.viewCount ?? 0) - (a.viewCount ?? 0));

    return list;
  });

  readonly displayedSongs = computed(() => this.filteredSongs().slice(0, this.loaded()));

  readonly alphaGroups = computed<{ letter: string; songs: Lyric[] }[]>(() => {
    if (this.sort() !== 'alpha') return [];
    const map = new Map<string, Lyric[]>();
    for (const s of this.displayedSongs()) {
      const letter = /[a-zA-ZÀ-ÿ]/.test(s.title[0])
        ? s.title[0].normalize('NFD').replace(/[̀-ͯ]/g, '').toUpperCase()
        : '#';
      if (!map.has(letter)) map.set(letter, []);
      map.get(letter)!.push(s);
    }
    return Array.from(map.entries()).map(([letter, songs]) => ({ letter, songs }));
  });

  readonly metaForLang = computed(() => {
    const l = this.recueilLang();
    return this.recueilMeta().find((m) => m.langueCode === l) ?? null;
  });

  readonly recueilName = computed(
    () => this.recueilMeta()[0]?.recueilName ?? 'Recueil',
  );

  // Image du recueil servie depuis les assets (comme dans ls-recueil-grid), pas depuis imagePathUrl (http)
  readonly recueilImageUrl = computed(() => {
    const imagePath = this.lyricByRecueils()[0]?.recueil?.imagePath;
    return imagePath ? `/assets/${imagePath}` : null;
  });

  /** Code du recueil (filtre figé de la recherche serveur), lu sur ses chants. */
  private readonly recueilCode = computed(() => this.lyricByRecueils().find((l) => l.recueilCode != null)?.recueilCode ?? null);

  readonly recueilColor = computed(() => this.recueilMeta()[0]?.color);

  protected spineColor(): string {
    const color = this.recueilColor();
    return color != null ? this.colorService.convertToHex(color) : '#1456BE';
  }

  readonly countFr = computed(() => this.recueilMeta().find((m) => m.langueCode.toLowerCase() === 'fr')?.count ?? 0);
  readonly countHt = computed(() => this.recueilMeta().find((m) => m.langueCode.toLowerCase() === 'ht')?.count ?? 0);

  private static readonly LANG_META: Record<string, { label: string; flagClass: string }> = {
    fr: { label: 'Français', flagClass: 'flag-fr' },
    ht: { label: 'Krèyol', flagClass: 'flag-ht' },
    en: { label: 'English', flagClass: 'flag-en' },
  };

  readonly langTabs = computed(() =>
    this.recueilMeta().map((m) => {
      const code = m.langueCode.toLowerCase();
      return {
        code: code as Lang,
        label: RecueilComponent.LANG_META[code]?.label ?? m.langue,
        flagClass: RecueilComponent.LANG_META[code]?.flagClass ?? '',
        count: m.count,
      };
    }),
  );

  readonly resnote = computed(() => {
    const shown = Math.min(this.loaded(), this.filteredSongs().length);
    const total = this.filteredSongs().length;
    return `${shown} chargés · ${total} au total`;
  });

  readonly activeFilterCount = computed(() => {
    const f = this.filters();
    return (f.chorale !== 'all' ? 1 : 0) + (f.audio ? 1 : 0) + (f.sheet ? 1 : 0);
  });

  readonly allLoaded = computed(() => this.loaded() >= this.filteredSongs().length);

  // ── Scroll infini ──────────────────────────────────────────────────────────
  private observer?: IntersectionObserver;

  constructor() {
    super();
    effect(() => {
      const first = this.langTabs()[0]?.code;
      if (first && first !== 'fr') {
        const current = untracked(() => this.recueilLang());
        if (current === 'fr') this.recueilLang.set(first);
      }
    });

    // Recherche Meilisearch figée sur le recueil : le terme saisi (débouncé) l'alimente, le reste (langue, filtres,
    // tri) continue d'être appliqué ici.
    effect(() => {
      const q = this.query().trim();
      const code = this.recueilCode();
      untracked(() => this.scheduleServerSearch(q, code));
    });

    effect(() => {
      const name = this.recueilName();
      const lang = this.appLang();
      const id = this._recueilParam();
      if (!name) return;
      this.breadcrumbService.set([
        { label: 'Découvrir', routerLink: ['/', lang, EnumRouteName.Discover] },
        { label: 'Recueils',  routerLink: ['/', lang, this.basePath] },
        { label: name,        routerLink: ['/', lang, this.basePath, id] },
      ]);
    });

    afterNextRender(() => {
      if (isPlatformBrowser(this.platformId)) {
        void this.lyricService.getByRecueil(this._recueilParam());
      }
    });
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    this.ngZone.runOutsideAngular(() => {
      document.addEventListener('keydown', this.onKeydown);
    });

    const sentinel = this.host.nativeElement.querySelector('#sentinel') as HTMLElement | null;
    if (!sentinel) return;

    this.ngZone.runOutsideAngular(() => {
      this.observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !this.loading() && !this.allLoaded()) {
            this.ngZone.run(() => void this.loadMore());
          }
        },
        { rootMargin: '320px' },
      );
      this.observer.observe(sentinel);
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
    clearTimeout(this.searchTimer);
    if (isPlatformBrowser(this.platformId)) {
      document.removeEventListener('keydown', this.onKeydown);
    }
  }

  private readonly onKeydown = (e: KeyboardEvent): void => {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
      e.preventDefault();
      this.ngZone.run(() => this.searchInput?.nativeElement.focus());
    }
  };

  private searchTimer?: ReturnType<typeof setTimeout>;
  private searchSeq = 0;

  private scheduleServerSearch(query: string, recueilCode: number | null): void {
    clearTimeout(this.searchTimer);
    const seq = ++this.searchSeq;
    if (!query || recueilCode == null || !isPlatformBrowser(this.platformId)) {
      this.matchedIds.set(null);
      return;
    }
    this.searchTimer = setTimeout(() => void this.runServerSearch(query, recueilCode, seq), SEARCH_DEBOUNCE_MS);
  }

  private async runServerSearch(query: string, recueilCode: number, seq: number): Promise<void> {
    try {
      const ids = new Set<string>();
      let total = Infinity;
      for (let page = 1; ids.size < total && page <= MAX_SEARCH_PAGES; page++) {
        const res = await firstValueFrom(
          this.searchService.searchLyricsFiltered({
            query, recueils: [recueilCode], languages: [], genres: [], styles: [], categories: [], keys: [],
            bpmMin: 60, bpmMax: 180, hasAudio: false, hasPartition: false, sortBy: 'relevance',
            page, limit: SEARCH_PAGE_SIZE,
          }),
        );
        // Aucun chant du recueil ne contient tous les mots : les « suggestions » du serveur ne sont pas des résultats ici.
        if (res.isApproximate) break;
        res.hits.forEach((hit) => ids.add(hit.id));
        total = res.totalHits;
        if (!res.hits.length) break;
      }
      if (seq === this.searchSeq) this.matchedIds.set(ids);
    } catch {
      // Recherche serveur indisponible : le filtre local sur le titre reste actif.
      if (seq === this.searchSeq) this.matchedIds.set(null);
    }
  }

  private async loadMore(): Promise<void> {
    this.loading.set(true);
    await new Promise((r) => setTimeout(r, 480));
    this.loaded.update((n) => n + BATCH);
    this.loading.set(false);
  }

  private resetLoaded(): void {
    this.loaded.set(BATCH);
  }

  // ── Handlers ───────────────────────────────────────────────────────────────

  protected setLang(l: Lang): void {
    this.recueilLang.set(l);
    this.query.set('');
    this.resetLoaded();
  }

  protected setQuery(value: string): void {
    this.query.set(value);
    this.resetLoaded();
  }

  protected setSort(s: Sort): void {
    this.sort.set(s);
    this.sortMenuOpen.set(false);
    this.resetLoaded();
  }

  protected setChorale(v: 'all' | 'none' | 'with'): void {
    this.filters.update((f) => ({ ...f, chorale: v }));
    this.resetLoaded();
  }

  protected toggleAudio(): void {
    this.filters.update((f) => ({ ...f, audio: !f.audio }));
    this.resetLoaded();
  }

  protected toggleSheet(): void {
    this.filters.update((f) => ({ ...f, sheet: !f.sheet }));
    this.resetLoaded();
  }

  protected resetFilters(): void {
    this.filters.set({ chorale: 'none', audio: false, sheet: false });
    this.filterMenuOpen.set(false);
    this.resetLoaded();
  }

  protected setView(v: View): void {
    this.view.set(v);
  }

  protected closeMenus(): void {
    this.filterMenuOpen.set(false);
    this.sortMenuOpen.set(false);
  }

  readonly sortOptions: { value: Sort; label: string }[] = [
    { value: 'num', label: 'Numéro de recueil' },
    { value: 'alpha', label: 'Titre (A→Z)' },
    { value: 'pop', label: 'Popularité' },
  ];

  protected sortLabel(): string {
    const map: Record<Sort, string> = {
      num: 'Numéro',
      alpha: 'Titre A→Z',
      pop: 'Popularité',
    };
    return map[this.sort()];
  }
}
